import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {GroupData} from '@/types/group';
import {EventData} from '@/types/event';
import {Location} from '@/schemas/sub-schemas';

export interface GroupCreateFormState {
  title: string;
  description: string;
  sports: string | string[] | null;
  accessType: string;
  location: Location | null;
  companyRole: string | null;
  picture: string | null;
  updatedAt: string | null;
  company?: string | undefined;
  trainerGroup?: boolean;
}
interface GroupState {
  myGroups: GroupData[];
  groupCreateForm: GroupCreateFormState;
}

export const initialState: GroupState = {
  myGroups: [],
  groupCreateForm: {
    title: '',
    description: '',
    sports: [],
    accessType: 'PUBLIC',
    location: null,
    companyRole: null,
    picture: null,
    updatedAt: null,
    company: undefined,
    trainerGroup: false
  }
};

const sortGroupsAlphabetically = (groups: GroupData[]) => {
  return groups.sort((a, b) => a.title.localeCompare(b.title));
};

const groupSlice = createSlice({
  name: 'group',
  initialState,
  reducers: {
    setGroupsInRedux(state, action: PayloadAction<GroupData[]>) {
      const uniqueGroups = Array.from(
        new Map(action.payload.map((group) => [group._id, group])).values()
      );
      state.myGroups = sortGroupsAlphabetically(uniqueGroups);
    },

    updateMyGroups(
      state,
      action: PayloadAction<{group: GroupData; userId: string}>
    ) {
      const {group, userId} = action.payload;
      // if userIsMember && not in myGroups, add to mygroup
      // else if !userIsMember && inMyGroups, remove from myGroups

      // if (!group || !group.members) {
      //   return state;
      // }

      const isMember = group.members.findIndex((m) => m._id === userId) >= 0;
      const isManager = group.managers.findIndex((m) => m._id === userId) >= 0;
      const isModerator =
        group.moderators?.findIndex((m) => m._id === userId) >= 0;

      const newGroups = [...state.myGroups];
      const isInMyGroups = newGroups.findIndex((g) => g._id === group._id);

      if (isMember) {
        if (isInMyGroups < 0) {
          newGroups.push(group);
        } else {
          newGroups.splice(isInMyGroups, 1, group);
        }
        // } else if (isInMyGroups >= 0) {
      } else if (isInMyGroups >= 0) {
        if (isManager || isModerator) {
          newGroups.splice(isInMyGroups, 1, group);
        } else {
          newGroups.splice(isInMyGroups, 1);
        }
      }
      return {
        ...state,
        myGroups: sortGroupsAlphabetically(newGroups)
      };
    },
    removeGroup(state, action: PayloadAction<string>) {
      state.myGroups = state.myGroups.filter(
        (group) => group._id !== action.payload
      );
    },

    updateGroupInRedux(state, action: PayloadAction<GroupData>) {
      const index = state.myGroups.findIndex(
        (group) => group._id === action.payload._id
      );
      if (index !== -1) {
        state.myGroups[index] = action.payload;
        state.myGroups = sortGroupsAlphabetically(state.myGroups);
      }
    },
    removeAllGroups(state) {
      state.myGroups = [];
    },

    addEventToGroup(
      state,
      action: PayloadAction<{groupId: string; event: EventData}>
    ) {
      const {groupId, event} = action.payload;
      const group = state.myGroups.find((group) => group._id === groupId);

      if (group) {
        group.events.push(event);
      }
    },

    updateEventInGroup(
      state,
      action: PayloadAction<{groupId: string; event: EventData}>
    ) {
      const {groupId, event} = action.payload;
      const group = state.myGroups.find((group) => group._id === groupId);
      if (group && group.events) {
        const index = group.events.findIndex((e) => e._id === event._id);
        if (index !== -1) {
          group.events[index] = event;
        }
      }
    },

    deleteEventFromGroup(
      state,
      action: PayloadAction<{groupId: string; eventId: string}>
    ) {
      const {groupId, eventId} = action.payload;
      const group = state.myGroups.find((group) => group._id === groupId);
      if (group) {
        group.events = group.events.filter((event) => event._id !== eventId);
      }
    },

    setGroupFormData(
      state,
      action: PayloadAction<Partial<GroupCreateFormState>>
    ) {
      state.groupCreateForm = {
        ...state.groupCreateForm,
        ...action.payload
      };
    },

    resetGroupFormData(state) {
      state.groupCreateForm = {
        ...initialState.groupCreateForm
      };
    }
  }
});

export const {
  setGroupsInRedux,
  updateGroupInRedux,
  removeGroup,
  removeAllGroups,
  addEventToGroup,
  deleteEventFromGroup,
  updateEventInGroup,
  updateMyGroups,
  setGroupFormData,
  resetGroupFormData
} = groupSlice.actions;
export default groupSlice.reducer;
